import { Button, DatePicker, Input, Select, Space, TableProps } from "antd";
import { Link } from "react-router-dom";
import { useAppSelector } from "../../../../app/store";
import ActionWatchIcon from "../../../../assets/icons/ActionWatchIcon";
import DateIcon from "../../../../assets/icons/DateIcon";
import SearchIcon from "../../../../assets/icons/SearchIcon";
import SelectDownIcon from "../../../../assets/icons/SelectDownIcon";
import ViewIcon from "../../../../assets/icons/ViewIcon";
import LogViewModalKPI from "../../../../components/OwnKPI/LogViewModalKPI";
import CustomTable from "../../../../components/ui/CustomTable";
import { dateFormat, dbDateFormat } from "../../../../constants/constants";
import { useGetOwnKpiByDepartmentQuery } from "../../../../features/performanceManagement/performnceManagementApi";
import useError from "../../../../hooks/useError";
import useModal from "../../../../hooks/useModal";
import useTable from "../../../../hooks/useTable";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import { formatDate } from "../../../../lib/time";
import {
  GetOwnKpiDepartmentParams,
  GetOwnKpiDepartmentResponseType,
} from "../../../../types/performance";
import { countParPageSerialNumber } from "../../../../utils/number";
import { addKeyInArray, selectSearchOption } from "../../../../utils/utils";
import { findPlanVal } from "../OpenKPISetup/OpenKPISetup";
import {
  planPeriodList,
  planTypeList,
} from "../OpenKPISetup/OpenKPISetupCreate";
import { statusFilterList } from "../TeamMemberKPI/TeamMemberKPI";

const { RangePicker } = DatePicker;

type OwnKpiListType = GetOwnKpiDepartmentResponseType["data"]["kpis"][0];

export const planTypeColor = {
  performance: "#D8A800",
  development: "#4A72FF",
};

export const statusList = {
  pending: {
    text: "Pending for Approval",
    style: "status-warning",
  },
  complete: {
    text: "Approved by Line Manager",
    style: "status-success",
  },
  reject: {
    text: "Reject by Line Manager",
    style: "status-danger",
  },
  default: {
    text: "Appraisal Start",
    style: "status text-[#4A72FF] bg-[#F2F5FF]",
  },
  not_start: {
    text: "Not Started",
    style: "status text-[#4a4a4d] bg-[#F2F5FF]",
  },
};

export default function OwnAppraisals() {
  const { open, handleClose, handleOpen } = useModal<
    { log: any; kpi: string },
    string
  >();
  const { authInfo } = useAppSelector((state) => state.auth);
  const departmentId = authInfo?.departmentId;
  const {
    page,
    limit,
    handlePagination,
    handleLimit,
    handleSearch,
    filter,
    handleFilter,
    keyword,
  } = useTable<GetOwnKpiDepartmentParams>({
    defaultFilter: {
      departmentId,
      positionStatus: "appraisal",
      employeeId: authInfo?.id,
    },
  });
  const { isLoading, isError, error, data } = useGetOwnKpiByDepartmentQuery({
    params: {
      ...filter,
      search: keyword,
    },
  });
  useError(isError, error, "Get own Appraisal fail");

  const columns: TableProps<OwnKpiListType>["columns"] = [
    {
      title: "#SL",
      align: "center",
      width: 60,
      render: (_, __, index) => countParPageSerialNumber(page, limit, index),
    },
    {
      title: "Plan Name",
      dataIndex: "kpi_title",
      render: (val) => val || "n/a",
    },

    {
      title: "Plan Period",
      dataIndex: "plan_period",
      render: (val) => findPlanVal(planPeriodList, val),
    },
    {
      title: "Plan Type",
      dataIndex: "plan_type",
      render: (val: keyof typeof planTypeColor) => (
        <span style={planTypeColor[val] ? { color: planTypeColor[val] } : {}}>
          {findPlanVal(planTypeList, val)}
        </span>
      ),
    },
    {
      title: "Status",
      // dataIndex: "employeeKpis",
      width: 300,
      render: (_, record) => {
        const status = record?.employeeKpis?.length
          ? record?.employeeKpis?.[0]?.statusAppraisal || "default"
          : "not_start";
        const st = statusList[status];
        return <span className={st?.style}>{st?.text}</span>;
      },
    },
    {
      title: "Due Date",
      render: (_, record) => {
        const findDepartment = record?.appraisalDepartments?.find(
          (item) => item?.departmentId === departmentId
        );

        return findDepartment?.due_date
          ? formatDate(findDepartment?.due_date)
          : "n/a";
      },
    },

    {
      title: "Action",
      render: (_, record) => (
        <Space>
          <Link
            to={`/admin/kpi-setup/own-appraisals/view/${record?.employeeKpis?.[0]?.id}`}
          >
            <Button
              className="action-button"
              size="small"
              icon={<ViewIcon />}
            />
          </Link>

          <Button
            icon={<ActionWatchIcon />}
            size="small"
            className="action-button"
            onClick={() =>
              handleOpen({
                type: "OWN_KPI_LOG_VIEW",
                data: {
                  log: record?.actionLogs,
                  kpi: record?.kpi_title,
                },
              })
            }
          />
        </Space>
      ),
    },
  ];

  return (
    <>
      <SidebarLayout title="Own Appraisal List">
        {open.type === "OWN_KPI_LOG_VIEW" && (
          <LogViewModalKPI
            open={open.open}
            data={open?.data}
            handleClose={handleClose}
          />
        )}

        <CustomTable
          loading={isLoading}
          limit={limit}
          page={page}
          columns={columns}
          total={data?.data?.totalLength}
          dataSource={addKeyInArray(data?.data?.kpis)}
          handleLimit={handleLimit}
          handlePagination={handlePagination}
          filterSection={
            <>
              <Input
                suffix={<SearchIcon />}
                placeholder={`Search...`}
                style={{ minWidth: 170, maxWidth: 180 }}
                onChange={handleSearch}
                allowClear
              />

              <Select
                allowClear
                showSearch
                onChange={(val) => handleFilter("status", val)}
                placeholder="Status"
                filterOption={selectSearchOption}
                options={statusFilterList}
                className="min-w-[130px]"
                suffixIcon={<SelectDownIcon />}
                rootClassName="icon-gray"
                dropdownStyle={{ minWidth: "230px" }}
              />

              <RangePicker
                format={dateFormat}
                style={{ width: 260 }}
                suffixIcon={<DateIcon />}
                onChange={(e) => {
                  handleFilter("startDate", e?.[0]?.format(dbDateFormat));
                  handleFilter("endDate", e?.[1]?.format(dbDateFormat));
                }}
              />
              {/* <Button
                type="primary"
                className="middle-button !p-4"
                icon={<SettingIcon />}
              /> */}
            </>
          }
        />
      </SidebarLayout>
    </>
  );
}

// import React from "react";
// import SidebarLayout from "../../../../layouts/SidebarLayout";
// import { Link, useLocation } from "react-router-dom";
// import useTable from "../../../../hooks/useTable";
// import { Button, DatePicker, Input, Space, TableProps } from "antd";
// import { countParPageSerialNumber } from "../../../../utils/number";
// import ViewIcon from "../../../../assets/icons/ViewIcon";
// import OrangePlusIcon from "../../../../assets/icons/OrangePlusIcon";
// import ActionWatchIcon from "../../../../assets/icons/ActionWatchIcon";
// import CustomTable from "../../../../components/ui/CustomTable";
// import { addKeyInArray } from "../../../../utils/utils";
// import SearchIcon from "../../../../assets/icons/SearchIcon";
// import { dateFormat } from "../../../../constants/constants";
// import DateIcon from "../../../../assets/icons/DateIcon";
// import SettingIcon from "../../../../assets/icons/SettingIcon";
// import LogViewModal from "../../../../components/OwnAppraisals/LogViewModal";
// import useModal, { ModalType } from "../../../../hooks/useModal";
// import SubmitConfirmModal from "../../../../components/OwnAppraisals/SubmitConfirmModal";

// export default function OwnAppraisals() {
//   const { open, handleClose, handleOpen } = useModal<undefined, string>();

//   const { page, limit, handlePagination, handleLimit, keyword, handleSearch } =
//     useTable();

//   const columns: TableProps<ExpenseItem>["columns"] = [
//     {
//       title: "#SL",
//       align: "center",
//       width: 60,
//       render: (_, __, index) => countParPageSerialNumber(page, limit, index),
//     },
//     {
//       title: "Plan Name",
//       dataIndex: "planName",
//       render: (val) => val || "n/a",
//     },

//     {
//       title: "Plan Period",
//       dataIndex: "planPeriod",
//       render: (val) => val ?? "n/a",
//     },
//     {
//       title: "Plan Type",
//       dataIndex: "planType",
//       render: (val: string) => (
//         <span style={{ color: val === "Development" ? "#4A72FF" : "#D8A800" }}>
//           {val ?? "n/a"}
//         </span>
//       ),
//     },
//     {
//       title: "Status",
//       dataIndex: "status",
//       render: (val: string) => {
//         if (val === "Not Started") {
//           return (
//             <div className="px-2 py-0.5 bg-slate-100 rounded-md inline-flex justify-center items-center gap-2.5">
//               <div className="justify-start text-slate-500 text-xs font-medium">
//                 Not Started
//               </div>
//             </div>
//           );
//         }

//         if (val === "Appraisal Start") {
//           return (
//             <div className="px-2 py-0.5 bg-blue-50 rounded-md inline-flex justify-center items-center gap-2.5">
//               <div className="justify-start text-blue-500 text-xs font-medium">
//                 Appraisal Start
//               </div>
//             </div>
//           );
//         }

//         if (val === "KPI Setup Start") {
//           return (
//             <div className="px-2 py-0.5 bg-red-50 rounded-md inline-flex justify-center items-center gap-2.5">
//               <div className="justify-start text-red-500 text-xs font-medium">
//                 KPI Setup Start
//               </div>
//             </div>
//           );
//         }

//         if (val === "Pending for Approval") {
//           return (
//             <div className="px-2 py-0.5 bg-yellow-50 rounded-md inline-flex justify-center items-center gap-2.5">
//               <div className="justify-start text-yellow-500 text-xs font-medium">
//                 Pending for Approval
//               </div>
//             </div>
//           );
//         }

//         return (
//           <div className="px-2 py-0.5 bg-green-500/10 rounded-md inline-flex justify-center items-center gap-2.5">
//             <div className="justify-start text-green-600 text-xs font-medium">
//               {val || "n/a"}
//             </div>
//           </div>
//         );
//       },
//     },
//     {
//       title: "Due Date",
//       dataIndex: "dueDate",
//       render: (val) => val ?? "n/a",
//     },

//     {
//       title: "Action",
//       align: "center",
//       render: (_, record) => (
//         <Space>
//           <Button
//             onClick={() =>
//               handleOpen({
//                 type: "OWN_APP_LOG_VIEW",
//               })
//             }
//             className="action-button"
//             size="small"
//             icon={<ViewIcon />}
//           />

//           <Button
//             icon={<ActionWatchIcon />}
//             size="small"
//             className="action-button"
//           />
//         </Space>
//       ),
//     },
//   ];

//   return (
//     <>
//       <SidebarLayout title="Own Appraisals">
//         {open.type === "OWN_APP_LOG_VIEW" && (
//           <LogViewModal open={open.open} handleClose={handleClose} />
//         )}

//         <CustomTable
//           loading={false}
//           limit={limit}
//           page={page}
//           columns={columns}
//           total={100}
//           dataSource={addKeyInArray(fakeExpenses)}
//           handleLimit={handleLimit}
//           handlePagination={handlePagination}
//           filterSection={
//             <>
//               <Input
//                 // key={filter.search_type}
//                 suffix={<SearchIcon />}
//                 placeholder={`Search...`}
//                 style={{ minWidth: 170, maxWidth: 180 }}
//                 // onChange={(val) => handleSearch(val)}
//               />

//               <DatePicker
//                 format={dateFormat as unknown as string}
//                 style={{ width: 140 }}
//                 suffixIcon={<DateIcon />}
//                 className="clear-button-hide"
//                 // value={filter?.date ? dayjs(filter?.date) : null}
//                 // onChange={(date) => {
//                 //   const formattedDate = date?.format(dbDateFormat);
//                 //   handleFilter("date", formattedDate);
//                 // }}
//               />
//               <Link to="/admin/kpi-setup/own-appraisals/create">
//                 <Button
//                   type="primary"
//                   className="middle-button !p-4"
//                   icon={<SettingIcon />}
//                 />
//               </Link>
//             </>
//           }
//         />
//       </SidebarLayout>
//     </>
//   );
// }

// type ExpenseItem = {
//   planName: string;
//   planPeriod: string;
//   planType: string;
//   status: string;
//   dueDate: string;
//   business_member_id: string;
// };

// const fakeExpenses: ExpenseItem[] = [
//   {
//     planName: "Q1 2025",
//     planPeriod: "02 Jan, 2025",
//     planType: "Performance",
//     status: "Appraisal Start",
//     dueDate: "02 Jan, 2025",
//     business_member_id: "BM-001",
//   },
//   {
//     planName: "Q1 2025",
//     planPeriod: "02 Jan, 2025",
//     planType: "Performance",
//     status: "KPI Setup Start",
//     dueDate: "02 Jan, 2025",
//     business_member_id: "BM-001",
//   },
//   {
//     planName: "Q1 2025",
//     planPeriod: "02 Jan, 2025",
//     planType: "Development",
//     status: "Pending for Approval",
//     dueDate: "02 Jan, 2025",
//     business_member_id: "BM-002",
//   },
//   {
//     planName: "Q1 2025",
//     planPeriod: "02 Jan, 2025",
//     planType: "Performance",
//     status: "Approved by Line Manager",
//     dueDate: "02 Jan, 2025",
//     business_member_id: "BM-001",
//   },
//   {
//     planName: "Q1 2025",
//     planPeriod: "02 Jan, 2025",
//     planType: "Performance",
//     status: "Not Started",
//     dueDate: "02 Jan, 2025",
//     business_member_id: "BM-001",
//   },
// ];
